import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveActivity } from '../utils/recentActivities';
import './MeetingRoomSearch.css';

const MeetingRoomSearch = () => {
  const navigate = useNavigate();
  const locationRef = useRef(null);
  const dateInputRef = useRef(null);
  const [searchData, setSearchData] = useState({
    location: '',
    date: '',
    startTime: '',
    endTime: '',
    participants: 1,
  });
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [errors, setErrors] = useState({});

  // Sample location data - replace with API call
  const locations = [
    'Bangalore - Koramangala',
    'Bangalore - Whitefield',
    'Bangalore - Indiranagar',
    'Bangalore - Electronic City',
    'Mumbai - BKC',
    'Mumbai - Andheri',
    'Delhi - Connaught Place',
    'Delhi - Cyber City',
    'Hyderabad - HITEC City',
    'Pune - Hinjewadi',
  ];

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const participantOptions = [1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50];
  const datePattern = /^\d{4}-\d{2}-\d{2}$/;
  const openDatePicker = () => {
    if (dateInputRef.current?.showPicker) {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current?.focus();
    }
  };

  // Handle location input change
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSearchData({ ...searchData, location: value });
    
    if (value.length > 0) {
      const filtered = locations.filter(loc =>
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setLocationSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Select location from suggestions
  const selectLocation = (location) => {
    setSearchData({ ...searchData, location });
    setShowSuggestions(false);
  };

  // Validate time slots
  const validateTimeSlots = () => {
    const newErrors = {};
    
    if (!searchData.location) {
      newErrors.location = 'Please select a location';
    }
    if (!searchData.date) {
      newErrors.date = 'Please select a date';
    } else if (!datePattern.test(searchData.date)) {
      newErrors.date = 'Please select a valid date';
    }
    if (!searchData.startTime) {
      newErrors.startTime = 'Please select start time';
    }
    if (!searchData.endTime) {
      newErrors.endTime = 'Please select end time';
    }
    
    if (searchData.startTime && searchData.endTime) {
      const start = parseInt(searchData.startTime.replace(':', ''));
      const end = parseInt(searchData.endTime.replace(':', ''));
      
      if (start >= end) {
        newErrors.endTime = 'End time must be after start time';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    if (validateTimeSlots()) {
      // Save activity to recent activities
      const params = new URLSearchParams({
        location: searchData.location,
        date: searchData.date,
        startTime: searchData.startTime,
        endTime: searchData.endTime,
        participants: searchData.participants,
      });
      
      // Save activity
      saveActivity({
        location: searchData.location,
        date: searchData.date,
        startTime: searchData.startTime,
        endTime: searchData.endTime,
        participants: searchData.participants,
        searchParams: params.toString()
      });
      
      // Navigate to search results with query parameters
      navigate(`/meetings/search-results?${params.toString()}`);
    }
  };

  return (
    <div className="meeting-search-container">
      <form onSubmit={handleSearch} className="meeting-search-form">
        <div className="search-fields-grid">
          {/* Location Search */}
          <div className="search-field location-field" ref={locationRef}>
            <label className="search-label">
              <svg className="field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Location
            </label>
            <input
              type="text"
              placeholder="City, area, or building"
              value={searchData.location}
              onChange={handleLocationChange}
              className={`search-input ${errors.location ? 'error' : ''}`}
            />
            {errors.location && <span className="error-text">{errors.location}</span>}
            
            {showSuggestions && locationSuggestions.length > 0 && (
              <div className="location-suggestions">
                {locationSuggestions.map((loc, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => selectLocation(loc)}
                  >
                    <svg className="suggestion-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    {loc}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date Picker */}
          <div className="search-field">
            <label className="search-label">
              <svg
                className="field-icon icon-clickable"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="button"
                tabIndex={0}
                onClick={openDatePicker}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openDatePicker();
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Date
            </label>
            <input
              ref={dateInputRef}
              type="date"
              value={searchData.date}
              onChange={(e) => setSearchData({ ...searchData, date: e.target.value })}
              onClick={openDatePicker}
              className={`search-input ${errors.date ? 'error' : ''}`}
            />
            {errors.date && <span className="error-text">{errors.date}</span>}
          </div>

          {/* Start Time */}
          <div className="search-field">
            <label className="search-label">
              <svg className="field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Start Time
            </label>
            <select
              value={searchData.startTime}
              onChange={(e) => setSearchData({ ...searchData, startTime: e.target.value })}
              className={`search-input ${errors.startTime ? 'error' : ''}`}
            >
              <option value="">Select time</option>
              {timeSlots.map((time) => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
            {errors.startTime && <span className="error-text">{errors.startTime}</span>}
          </div>

          {/* End Time */}
          <div className="search-field">
            <label className="search-label">
              <svg className="field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              End Time
            </label>
            <select
              value={searchData.endTime}
              onChange={(e) => setSearchData({ ...searchData, endTime: e.target.value })}
              className={`search-input ${errors.endTime ? 'error' : ''}`}
            >
              <option value="">Select time</option>
              {timeSlots.map((time) => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
            {errors.endTime && <span className="error-text">{errors.endTime}</span>}
          </div>

          {/* Participants */}
          <div className="search-field">
            <label className="search-label">
              <svg className="field-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Participants
            </label>
            <select
              value={searchData.participants}
              onChange={(e) => setSearchData({ ...searchData, participants: parseInt(e.target.value, 10) || 1 })}
              className="search-input"
            >
              {participantOptions.map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <div className="search-field search-button-field">
            <button type="submit" className="search-button">
              <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Rooms
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MeetingRoomSearch;
